/* eslint-disable @typescript-eslint/prefer-optional-chain */
import { messageError, setMessageLocked } from '../../components/message';
import { isSupportSystem } from '../../config';
import getFormatMessage from '../../locales/get-format-message';
import { BackEndCode } from '../../model/api-modules';
import { getSupportCookieToken } from '../../pages/admin/lib/ioa-helper';
import { getLoginToken, jumpToLogin } from '../../pages/lib';
import { reportPageError } from '../../pages/lib/bk-report';
// import { ErrCodeMsgMap } from '../consts/err-msg';
import { log } from '../log';
import { startTimer } from '../timing';
import { urlWithQueryString } from '../url';
import { getApiErrorMsg } from './api-error-msg';

export type HTTPMethod = 'GET' | 'POST' | 'PUT' | 'HEAD' | 'DELETE' | 'PATCH';

export interface IError {
  readonly message: string;
  readonly resource: string;
  readonly field: string;
}

export interface IAPIError {
  readonly errors?: IError[];
  readonly message?: string;
  readonly code?: number;
  readonly data?: any;
}

export interface IAPIUGitServerResponseBody {
  readonly ret: number;
  readonly msg: string;
}
const isDev = process.env.REACT_APP_ENV === 'development';
/** An error from getting an unexpected response to an API call. */
export class APIError extends Error {
  /** The error as sent from the API, if one could be parsed. */
  public readonly apiError: IAPIError | null;

  /** The HTTP response code that the error was delivered with */
  public readonly responseStatus: number;

  public constructor(response: Response, apiError: IAPIError | null) {
    super();
    this.responseStatus = response.status;
    this.apiError = {
      ...apiError,
      message: apiError?.message || this.message,
    };
    this.message = this.getErrMsg(response, apiError);
  }

  public readonly getErrMsg = (
    response: Response,
    apiError: IAPIError | null
  ) => {
    if (apiError && apiError.message) {
      const { errors } = apiError;
      const additionalMessages =
        errors && errors.map((e) => e.message).join(', ');
      if (additionalMessages) {
        return `${apiError.message} (${additionalMessages})`;
      }
      return apiError.message;
    }

    if (apiError?.code) {
      return getFormatMessage(String(apiError.code));
    }

    return getFormatMessage('api_error_message', {
      params: {
        url: response.url,
        statusText: response.statusText,
        status: response.status,
      },
    });
  };
}

/**
 * Deserialize the HTTP response body into an expected object shape
 *
 * Note: this doesn't validate the expected shape, and will only fail if it
 * encounters invalid JSON.
 */
async function deserialize<T>(response: Response): Promise<T> {
  try {
    const json = await response.json();
    return json as T;
  } catch (e) {
    const contentLength = response.headers.get('Content-Length') || '(missing)';
    log().warn(
      `deserialize: invalid JSON found at '${response.url}' - status: ${response.status}, length: '${contentLength}'`,
      e
    );
    throw e;
  }
}

const logErrorRecord = (error: Error) => {
  log().error(error);
};

/**
 * Make an API request.
 *
 * @param endpoint      - The API endpoint.
 * @param method        - The HTTP method.
 * @param path          - The path, including any query string parameters.
 * @param jsonBody      - The JSON body to send.
 * @param customHeaders - Any optional additional headers to send.
 * @param timeout       - The timeout of fetch request (ms).
 */
export function request(
  endpoint: string,
  method: HTTPMethod,
  path: string,
  jsonBody?: { [key: string]: any },
  customHeaders?: Object,
  timeout?: number
): Promise<Response> {
  const timingTimer = startTimer(`HTTP/${method}/${path}`);
  let url = `${isDev ? '' : endpoint}${path}`;
  let headers: any = {
    Accept: 'application/vnd.github.v3+json, application/json',
    'Content-Type': 'application/json',
  };

  if (isSupportSystem()) {
    headers.ticket = getSupportCookieToken();
  } else {
    const areaCookie = getLoginToken();
    if (areaCookie) {
      headers.ticket = areaCookie;
    }
  }

  headers = {
    ...headers,
    ...customHeaders,
  };

  const options = {
    headers,
    method,
    body: ['GET', 'HEAD'].includes(method)
      ? undefined
      : JSON.stringify(jsonBody),
  };

  // 请求统一json传值
  if (method === 'GET' && typeof jsonBody === 'object') {
    const reBody = JSON.parse(JSON.stringify(jsonBody));
    url = urlWithQueryString(url, reBody);
  }

  if (timeout) {
    let isTimeout = false;
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        isTimeout = true;
        const error = new Error(
          `${timingTimer.actionId}, ${path}, Request Timeout!`
        );
        logErrorRecord(error);
        reject();
      }, timeout);
      fetch(url, { ...options, credentials: 'include' })
        .then((res) => {
          timingTimer.done();
          clearTimeout(timer);
          if (!isTimeout) {
            resolve(res);
          }
        })
        .catch((err: Error) => {
          timingTimer.done();
          const newError = {
            ...err,
            message: `${timingTimer.actionId} ${err.message}`,
          };
          logErrorRecord(newError);
          reportPageError(err, path);
          if (!isTimeout) {
            reject(newError);
          }
        });
    });
  }

  return new Promise((resolve, reject) => {
    fetch(url, options)
      .then((res) => {
        timingTimer.done();
        if (res.status !== 200) {
          logErrorRecord(
            new Error(
              `${timingTimer.actionId}, ${res.status}, ${method}, ${path}`
            )
          );
        }
        resolve(res);
      })
      .catch((err: Error) => {
        timingTimer.done();
        const newError = {
          ...err,
          message: `${timingTimer.actionId} ${err.message}`,
        };
        reportPageError(newError, path);
        logErrorRecord(newError);
        reject(newError);
      });
  });
}

export function blobRequest(
  endpoint: string,
  method: HTTPMethod,
  path: string,
  jsonBody?: { [key: string]: any },
  customHeaders?: Object,
  timeout?: number
): Promise<ArrayBuffer> {
  const timingTimer = startTimer(`HTTP/${method}/${path}`);
  let url = `${isDev ? '' : endpoint}${path}`;
  let headers: any = {
    Accept: 'application/vnd.github.v3+json, application/json',
    'Content-Type': 'application/json',
  };

  const areaCookie = getLoginToken();
  if (areaCookie) {
    headers.ticket = areaCookie;
  }

  headers = {
    ...headers,
    ...customHeaders,
  };

  const options = {
    headers,
    method,
    body: ['GET', 'HEAD'].includes(method)
      ? undefined
      : JSON.stringify(jsonBody),
  };

  // 请求统一json传值
  if (method === 'GET' && typeof jsonBody === 'object') {
    const reBody = JSON.parse(JSON.stringify(jsonBody));
    url = urlWithQueryString(url, reBody);
  }

  if (timeout) {
    let isTimeout = false;
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        isTimeout = true;
        const error = new Error(
          `${timingTimer.actionId}, ${path}, Request Timeout!`
        );
        logErrorRecord(error);
        reject();
      }, timeout);
      fetch(url, options)
        .then((res) => res.arrayBuffer())
        .then((arrayBuffer) => {
          timingTimer.done();
          clearTimeout(timer);
          if (!isTimeout) {
            resolve(arrayBuffer);
          }
        })
        .catch((err: Error) => {
          timingTimer.done();
          const newError = {
            ...err,
            message: `${timingTimer.actionId} ${err.message}`,
          };
          logErrorRecord(newError);
          if (!isTimeout) {
            reject(newError);
          }
        });
    });
  }

  return new Promise((resolve, reject) => {
    fetch(url, options)
      .then((res) => {
        timingTimer.done();
        if (res.status !== 200) {
          logErrorRecord(
            new Error(
              `${timingTimer.actionId}, ${res.status}, ${method}, ${path}`
            )
          );
        }
        return res;
      })
      .then((res) => res.arrayBuffer())
      .then((arrayBuffer) => {
        resolve(arrayBuffer);
      })
      .catch((err: Error) => {
        timingTimer.done();
        const newError = {
          ...err,
          message: `${timingTimer.actionId} ${err.message}`,
        };
        logErrorRecord(newError);
        reject(newError);
      });
  });
}

// export function requestForm(
//   endpoint: string,
//   token: string | null,
//   method: HTTPMethod,
//   path: string,
//   jsonBody?: Object,
//   customHeaders?: Object
// ): Promise<Response> {
//   const params = { access_token: token as string }
//   const query = urlWithQueryString(path, token ? params : {})
//   let url = getAbsoluteUrl(endpoint, query)
//   if (
//     endpoint.includes('github.com') ||
//     isTapd(endpoint) ||
//     isCoding(endpoint)
//   ) {
//     url = getAbsoluteUrl(endpoint, path)
//   }
//   let headers: any = {
//     Accept:
//       'application/vnd.github.v3+json, application/json, application/x-www-form-urlencoded',
//     'Content-Type': 'application/x-www-form-urlencoded',
//     'User-Agent': getUserAgent(),
//   }

//   if (token) {
//     if (isTapd(endpoint) || isCoding(endpoint)) {
//       headers['Authorization'] = `Bearer ${token}`
//     } else {
//       headers['Authorization'] = `token ${token}`
//     }
//   }

//   headers = {
//     ...headers,
//     ...customHeaders,
//   }

//   const options = {
//     headers,
//     method,
//     body: jsonBody && paramsToStr(jsonBody),
//   }

//   return new Promise((resolve, reject) => {
//     return fetch(url, options)
//       .then(res => {
//         if (res.status !== 200) {
//           mountainsProfilerHttp(res, method, path, url, jsonBody)
//         }
//         resolve(res)
//       })
//       .catch(err => {
//         mountainsProfilerHttp(err, method, path, url, jsonBody)
//         reject(err)
//       })
//   })
// }

// export function requestFormData(
//   endpoint: string,
//   token: string | null,
//   method: HTTPMethod,
//   path: string,
//   body: {
//     fileName: string
//     file: Blob
//   },
//   customHeaders?: Object
// ): Promise<Response> {
//   const params = { access_token: token as string }
//   const query = urlWithQueryString(path, params)
//   let url = getAbsoluteUrl(endpoint, query)
//   if (endpoint.includes('github.com') || isTapd(endpoint)) {
//     url = getAbsoluteUrl(endpoint, path)
//   }
//   let headers: any = {
//     Accept: 'application/vnd.github.v3+json, multipart/form-data',
//     'User-Agent': getUserAgent(),
//   }

//   if (token) {
//     if (isTapd(endpoint) || isCoding(endpoint)) {
//       headers['Authorization'] = `Bearer ${token}`
//     } else {
//       headers['Authorization'] = `token ${token}`
//     }
//   }

//   headers = {
//     ...headers,
//     ...customHeaders,
//   }

//   const data = new FormData()
//   data.append(body.fileName, body.file)

//   const options = {
//     headers,
//     method,
//     body: data,
//   }

//   return fetch(url, options)
// }

/**
 * If the response was OK, parse it as JSON and return the result. If not, parse
 * the API error and throw it.
 */
// export async function parsedResponse<T>(response: Response): Promise<T> {
//   if (response.ok) {
//     return deserialize<T>(response)
//   } else {
//     let apiError: IAPIError | null
//     try {
//       apiError = await deserialize<IAPIError>(response)
//     } catch (e) {
//       throw new APIError(response, null)
//     }

//     throw new APIError(response, apiError)
//   }
// }

// 后台提供服务，建议统一json格式: {ret: number, msg: string, data: T}
// 当ret = 0时，返回参数建议放置于data下，目的可以定义类型
// // tapd status === 1 标识成功

const customError = (msg: string) => {
  messageError(msg);
  setMessageLocked(true);
  setTimeout(() => {
    setMessageLocked(false);
  }, 3000);
};

export async function parsedResponse<T>(
  response: Response,
  key?: string,
  isNotLogout?: boolean,
  notVerifyCode?: boolean // 不单独校验接口code 直接返回值
): Promise<T> {
  if (!isNotLogout && response.status === 401) {
    jumpToLogin();
    // 不处理401
    const result: Partial<T> = {};
    return result as T;
  }
  // 参数错误处理
  if (response.status === 400) {
    const json = await response.json();

    if (json.code === 101305) {
      // if (
      //   json.data &&
      //   Array.isArray(json.data.errors) &&
      //   json.data.errors.length > 0 &&
      //   Array.isArray(json.data.errors[0].results) &&
      //   json.data.errors[0].results.length > 0
      // ) {
      //   const firstResult = json.data.errors[0].results[0];
      //   const errorCode = firstResult.code;
      //   const { infoError, infoExpected } = firstResult;

      // if (errorCode === -1 || errorCode === -2) {
      //   let errorMessage = getFormatMessage('400');
      //   if (
      //     infoError &&
      //     typeof infoError === 'object' &&
      //     Object.keys(infoError).length > 0
      //   ) {
      //     const errorKeys = Object.keys(infoError);
      //     errorMessage += `,${infoError[errorKeys[0]]}`;
      //   } else if (json.data.errors[0].key) {
      //     errorMessage += `,${json.data.errors[0].key}`;
      //   }
      //   customError(errorMessage);
      //   throw new APIError(response, {
      //     message: errorMessage || json.msg,
      //     code: json.code,
      //     data: json.data,
      //   });
      // } else {
      //   if (
      //     infoError &&
      //     typeof infoError === 'object' &&
      //     Object.keys(infoError).length > 0
      //   ) {
      //     // 取第一个字段的 key 和 value
      //     const [fieldName, fieldValue] = Object.entries(infoError)[0];
      //     let errorMessage = getFormatMessage('parameter_custom_error', {
      //       params: {
      //         parameter: fieldName,
      //         errorValue: fieldValue,
      //       },
      //     });
      //     if (
      //       infoExpected &&
      //       typeof infoExpected === 'object' &&
      //       Object.keys(infoExpected).length > 0 &&
      //       infoExpected[fieldName]
      //     ) {
      //       errorMessage += ` ${getFormatMessage('parameter_expected', {
      //         params: {
      //           expectedValue: infoExpected[fieldName],
      //         },
      //       })}`;
      //     }
      //     customError(errorMessage);
      //     throw new APIError(response, {
      //       message: errorMessage,
      //       code: json.code,
      //       data: json.data,
      //     });
      //   }
      // }
      // }
      customError(getFormatMessage('400'));
      reportPageError(
        new Error(getFormatMessage('400') || json.msg),
        response.url
      );
      // 如果格式不对，也抛出通用参数错误
      throw new APIError(response, {
        message: getFormatMessage('400') || json.msg,
        code: json.code,
        data: json.data,
      });
    }
  }
  if (response.ok) {
    const json = await response.json();
    if (
      json.ret === 0 ||
      json.status === 1 ||
      json.code === 0 ||
      json.result === true ||
      BackEndCode[json.code] ||
      notVerifyCode
    ) {
      const newKey = key || 'data';
      if (newKey) {
        if (json[newKey] !== undefined) {
          return json[newKey] as T;
        }
        return json;
      }
    }
    const codeAsString = json.code.toString();
    // 能取到错误提示中英文
    if (getFormatMessage(codeAsString) !== codeAsString) {
      customError(getFormatMessage(codeAsString));
    }
    const error = new APIError(response, {
      // message: ErrCodeMsgMap[json.code] || json.msg,
      message: getApiErrorMsg(json) || json.msg,
      code: json.code,
      data: json.data,
    });
    reportPageError(error, response.url);
    throw error;
  }

  let apiError: IAPIError | null;
  try {
    apiError = await deserialize<IAPIError>(response);
  } catch (e) {
    const error = new APIError(response, null);
    reportPageError(error, response.url);
    throw error;
  }

  throw new APIError(response, apiError);
}

export async function parsedTGitResponse<T>(response: Response): Promise<T> {
  if (response.ok) {
    return deserialize<T>(response);
  }
  let apiError: IAPIError | null;
  // Deserializing the API error could throw. If it does, we'll throw a more
  // general API error.
  try {
    apiError = await deserialize<IAPIError>(response);
  } catch (e) {
    throw new APIError(response, null);
  }

  throw new APIError(response, apiError);
}

/*
export async function parsedResponseToBlob(
  response: Response
): Promise<Blob | null> {
  if (response.ok) {
    const json = response.blob();
    return json;
  }

  return null;
}

export function paramsToStr(params: any) {
  return Object.keys(params)
    .filter((key) => params[key])
    .map((key) => `${key}=${encodeURIComponent(params[key])}`)
    .join('&');
}
*/
